// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Loader, RefreshCw, Send } from 'lucide-react';
import {
  Badge,
  InfoBanner,
  workflowTableIconButtonEditClass,
  workflowTableIconButtonViewClass,
} from '../../components/WorkflowFormPrimitives';
import {
  AnalysisDocumentRow,
  DOCUMENT_KINDS,
  downloadAnalysisDocument,
  getAnalysisDocuments,
} from '../../../../services/AnalysisApiClient';
import { ApprovalEnvelopeRow, listEnvelopes } from '../../../../services/ApprovalEnvelopeApiClient';
import { excessForRow } from '../../utils/build280gAnalysisResults';
import { pushToast } from '../../../../utils/toast/toastStore';
import { isValidEmail } from '../../../../utils/email/isValidEmail';
import {
  envelopeStatusVariant,
  canResendWaiverEnvelope,
  envelopesByDocumentId,
  envelopeActivityTimestamp,
  formatEnvelopeTimestamp,
  hasInFlightWaiverEnvelopes,
  waiverSendDisabledTitle,
} from './envelopeUtils';
import { SendForSignatureDialog } from './SendForSignatureDialog';
import { fmt } from '../../utils/formatCurrency';

/**
 * 280G Waiver tab: per-DQI waiver document preview/download and DocuSign send.
 */
export function WaiverAgreementsPanel({ analysisId, runId, excessIndividuals, formData }) {
  const [documents, setDocuments] = useState<AnalysisDocumentRow[]>([]);
  const [envelopes, setEnvelopes] = useState<ApprovalEnvelopeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyDocId, setBusyDocId] = useState<string | null>(null);
  const [sendTarget, setSendTarget] = useState<AnalysisDocumentRow | null>(null);
  const [sendResend, setSendResend] = useState(false);

  const latestEnvelopeByDocId = useMemo(() => envelopesByDocumentId(envelopes), [envelopes]);

  const waiverDocumentIds = useMemo(
    () => documents.filter((d) => d.kind === DOCUMENT_KINDS.WaiverAgreement && d.status === 'Ready').map((d) => d.id),
    [documents]
  );

  const individualById = useMemo(() => {
    const map = new Map();
    (formData?.individuals || []).forEach((ind) => {
      if (ind?.id) map.set(ind.id, ind);
    });
    return map;
  }, [formData?.individuals]);

  const refresh = useCallback(async () => {
    if (!analysisId) {
      setDocuments([]);
      setEnvelopes([]);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const [docs, envs] = await Promise.all([
        getAnalysisDocuments(analysisId, { runId: runId ?? undefined, latestOnly: true }),
        listEnvelopes(analysisId),
      ]);
      setDocuments(docs);
      setEnvelopes(envs);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load waiver documents.');
      setDocuments([]);
      setEnvelopes([]);
    } finally {
      setLoading(false);
    }
  }, [analysisId, runId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!hasInFlightWaiverEnvelopes(envelopes, waiverDocumentIds)) return undefined;
    const id = window.setInterval(() => void refresh(), 15_000);
    return () => window.clearInterval(id);
  }, [envelopes, waiverDocumentIds, refresh]);

  const waiverDocForIndividual = useCallback(
    (individualId) =>
      documents.find(
        (d) => d.kind === DOCUMENT_KINDS.WaiverAgreement && d.status === 'Ready' && d.variant === individualId
      ) ?? null,
    [documents]
  );

  const handleDownload = async (doc) => {
    setBusyDocId(doc.id);
    try {
      await downloadAnalysisDocument(doc);
    } catch (e) {
      pushToast(e instanceof Error ? e.message : 'Download failed.', 'error');
    } finally {
      setBusyDocId(null);
    }
  };

  return (
    <div className="space-y-4">
      <InfoBanner variant="info" showIcon>
        Send each parachute payment waiver from this tab after reviewing the generated document. Shareholder disclosure
        and consent are sent automatically when you launch the Vote Room.
      </InfoBanner>

      {loadError ? (
        <InfoBanner variant="danger" showIcon>
          {loadError}
        </InfoBanner>
      ) : null}

      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-gray-900">Individuals Requiring Waiver</p>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading || !analysisId}
          aria-label="Refresh waiver status"
          className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
        >
          {loading ? <Loader size={13} className="mr-1 animate-spin" /> : <RefreshCw size={13} className="mr-1" />}
          Refresh
        </button>
      </div>

      <div className="space-y-3">
        {excessIndividuals.map((ind) => {
          const profile = individualById.get(ind.id);
          const waiverDoc = waiverDocForIndividual(ind.id);
          const envelope = waiverDoc ? (latestEnvelopeByDocId.get(waiverDoc.id) ?? null) : null;
          const signerEmail = String(profile?.email || '').trim();
          const hasSignerEmail = signerEmail.length > 0;
          const hasValidSignerEmail = isValidEmail(signerEmail);
          const canSend = !!waiverDoc && hasValidSignerEmail && canResendWaiverEnvelope(envelope);
          const sendDisabledTitle = waiverSendDisabledTitle(envelope, hasValidSignerEmail);
          const isBusy = busyDocId === waiverDoc?.id;
          const sendTitle = envelope ? 'Resend waiver for signature' : 'Send waiver for signature';
          const sendButtonTitle = canSend ? sendTitle : (sendDisabledTitle ?? sendTitle);
          const activity = envelope ? envelopeActivityTimestamp(envelope) : null;
          const openSendDialog = (doc, resend) => {
            setSendTarget(doc);
            setSendResend(resend);
          };

          return (
            <div key={ind.id} className="border border-red-100 rounded-lg bg-red-50/30 overflow-hidden">
              <div className="px-4 py-3 flex items-start justify-between gap-4">
                <div className="flex items-start space-x-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-medium text-red-700">{ind.name.charAt(0)}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{ind.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Base: {fmt(ind.base)} · Threshold: {fmt(ind.threshold)}
                    </p>
                    <p className="text-xs text-red-700 mt-0.5">{fmt(excessForRow(ind))} excess</p>
                    {!hasSignerEmail ? (
                      <p className="text-xs text-red-500 mt-1">Missing DQI email — add on Individuals step.</p>
                    ) : !hasValidSignerEmail ? (
                      <p className="text-xs text-red-500 mt-1" title={signerEmail}>
                        Invalid DQI email — fix on Individuals step.
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1 truncate" title={signerEmail}>
                        Signer: {signerEmail}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0 min-w-[7.5rem]">
                  <div className="text-right">
                    {envelope ? (
                      <>
                        <Badge variant={envelopeStatusVariant(envelope.status)}>{envelope.status}</Badge>
                        {activity?.at ? (
                          <p
                            className="mt-1 text-[11px] text-gray-500 tabular-nums truncate max-w-[11rem]"
                            title={`${activity.label} · ${activity.at}`}
                          >
                            {activity.label === 'Last Event' ? (
                              formatEnvelopeTimestamp(activity.at)
                            ) : (
                              <>
                                <span className="font-medium text-gray-600">{activity.label}</span>
                                {' · '}
                                {formatEnvelopeTimestamp(activity.at)}
                              </>
                            )}
                          </p>
                        ) : null}
                      </>
                    ) : waiverDoc ? (
                      <>
                        <Badge variant="default">Not Sent</Badge>
                        <p className="mt-1 text-[11px] text-gray-400">Ready to send</p>
                      </>
                    ) : (
                      <Badge variant="warning">No Document</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1.5">
                    {waiverDoc ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void handleDownload(waiverDoc)}
                          disabled={isBusy}
                          className={`${workflowTableIconButtonViewClass} disabled:opacity-40`}
                          title="Download waiver"
                          aria-label={`Download waiver for ${ind.name}`}
                        >
                          {isBusy ? (
                            <Loader size={16} className="animate-spin" aria-hidden />
                          ) : (
                            <Download size={16} strokeWidth={2} aria-hidden />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => openSendDialog(waiverDoc, !!envelope)}
                          disabled={!canSend}
                          className={`${workflowTableIconButtonEditClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                          title={sendButtonTitle}
                          aria-label={`${envelope ? 'Resend' : 'Send'} waiver for ${ind.name}`}
                          aria-disabled={!canSend}
                        >
                          <Send size={16} strokeWidth={2} aria-hidden />
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
              {waiverDoc ? (
                <div
                  className="px-4 py-2 border-t border-red-100 bg-white/70 text-xs text-gray-600 truncate"
                  title={waiverDoc.title}
                >
                  {waiverDoc.title}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {sendTarget ? (
        <SendForSignatureDialog
          document={sendTarget}
          resend={sendResend}
          onClose={() => {
            setSendTarget(null);
            setSendResend(false);
          }}
          onSent={() => {
            void refresh();
            setSendTarget(null);
            setSendResend(false);
          }}
          onFailed={() => {
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}
